/**
 * Practice setup: your head, the practice partner (an easy CPU), specials on/off → infinite downs. In the match
 * R / pad Back re-spots the ball and Tab switches between offense and defense (see the HUD practice strip).
 */
import {
  CHARACTER_IDS,
  characterById,
  randomSeed,
  SpecialsMode,
  specialInfo,
  type CharacterId,
} from '@hf/shared';
import { useState } from 'preact/hooks';
import { useActions, useServices } from '../actions';
import { Glove, Head, Key } from '../components';
import { buildOfflineSetup, draft } from '@hf/client-core/ui-model/flow';
import { useFocusGrid } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import { cycle, Shell, StepRow } from './Shell';

export function PracticeSetup() {
  const actions = useActions();
  const { audio } = useServices();
  const d = draft.peek();
  const [mine, setMine] = useState<CharacterId>(d.characters[0]);
  const [partner, setPartner] = useState<CharacterId>(d.characters[1]);
  const [specials, setSpecials] = useState<boolean>(true);
  const start = (): void => {
    playSfx(audio, UI_SFX.start);
    actions.unlockAudio();
    const setup = buildOfflineSetup(
      {
        ...draft.peek(),
        mode: 'practice',
        characters: [mine, partner],
        specials: specials ? SpecialsMode.SIGNATURE : SpecialsMode.OFF,
      },
      randomSeed(),
      ui.settings.peek().playerName,
    );
    actions.startMatch(setup);
  };
  const rows = [
    (dir: -1 | 1) => setMine((c) => cycle(CHARACTER_IDS, c, dir)),
    (dir: -1 | 1) => setPartner((c) => cycle(CHARACTER_IDS, c, dir)),
    () => setSpecials((s) => !s),
  ];
  const grid = useFocusGrid({
    count: rows.length + 1,
    initial: rows.length,
    onMove: () => playSfx(audio, UI_SFX.move),
    onAdjust: (i, dir) => {
      rows[i]?.(dir);
      playSfx(audio, UI_SFX.toggle);
    },
    onConfirm: (i) => (i === rows.length ? start() : i === 2 ? rows[2]!(1) : grid.setIndex(i + 1)),
    onBack: () => {
      playSfx(audio, UI_SFX.back);
      actions.goto('main_menu');
    },
  });
  const me = characterById(mine);
  return (
    <Shell
      screen="practice"
      title="PRACTICE"
      kicker="INFINITE DOWNS · NO CLOCK · NO SCORE"
      class="hf-practice-setup"
      onBack={() => actions.goto('main_menu')}
      hints={[
        ['left|right', 'CHANGE'],
        ['confirm', 'START'],
        ['back', 'BACK'],
      ]}
    >
      <div class="hf-setup__grid">
        <div class="hf-setup__rows">
          <StepRow
            label="YOUR HEAD"
            hint={me.role}
            value={me.name}
            focused={grid.index === 0}
            onStep={rows[0]!}
            item={grid.item(0)}
          />
          <StepRow
            label="PRACTICE PARTNER"
            hint="An easy CPU that plays it straight"
            value={characterById(partner).name}
            focused={grid.index === 1}
            onStep={rows[1]!}
            item={grid.item(1)}
          />
          <StepRow
            label="SPECIALS"
            hint={
              specials
                ? `Power stays full: try ${specialInfo(me.signature).name} on any play`
                : 'Pure moves only'
            }
            value={specials ? 'ON · FULL POWER' : 'OFF'}
            focused={grid.index === 2}
            onStep={() => rows[2]!(1)}
            item={grid.item(2)}
          />
          <button class="hf-btn hf-setup__go interactive" {...grid.item(3)}>
            START PRACTICE ▶
          </button>
        </div>
        <aside class="hf-matchcard hf-panel hf-practice-card">
          <div class="hf-matchcard__band t-broadcast">DRILL TIPS</div>
          <div class="hf-practice-card__heads" aria-hidden="true">
            <Head character={mine} expression="determined" class="hf-practice-card__head" key={mine} />
            <Glove side="home" pose="ready" class="hf-practice-card__glove home" />
            <span class="t-display">VS</span>
            <Glove side="away" pose="ready" class="hf-practice-card__glove away" />
            <Head
              character={partner}
              expression="determined"
              flip
              class="hf-practice-card__head"
              key={`p${partner}`}
            />
          </div>
          <ul>
            <li>
              <Key label="R" /> re-spots the ball any time — a fresh 1st & 10.
            </li>
            <li>
              <Key label="TAB" /> swaps sides so you can drill defense.
            </li>
            <li>Turnovers and scores just reset the drill. Nothing counts — experiment!</li>
            <li>Try the fake jump: jump, then press down while rising, then truck under the leap.</li>
          </ul>
        </aside>
      </div>
    </Shell>
  );
}
